import React, { useState } from "react";
import { Dropdown, Modal, Select, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { MenuProps } from "antd";

import UserAvatar from "../../components/UserAvatar";
import { PROJECT_MEMBER_ROLES } from "../../constants/projects.constants";

import type {
  ProjectMember,
  ProjectMemberRole,
} from "../../../../core/data/redux/projects/types";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";

interface MembersTableProps {
  members: ProjectMember[];
  loading: boolean;
  canManage?: boolean;
  canChangeRole?: boolean;
  onUpdateRole: (memberId: number, role: ProjectMemberRole) => Promise<void>;
  onRemove: (member: ProjectMember) => void;
}

const MembersTable: React.FC<MembersTableProps> = ({
  members,
  loading,
  canManage = false,
  canChangeRole = false,
  onUpdateRole,
  onRemove,
}) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingRole, setEditingRole] = useState<ProjectMemberRole>("MEMBER");
  const [savingId, setSavingId] = useState<number | null>(null);

  const startEdit = (m: ProjectMember) => {
    setEditingId(m.id);
    setEditingRole(m.role);
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveRole = async () => {
    if (editingId == null) return;
    try {
      setSavingId(editingId);
      await onUpdateRole(editingId, editingRole);
      setEditingId(null);
    } finally {
      setSavingId(null);
    }
  };

  const showActionsColumn = canManage || canChangeRole;
  const columns: ColumnsType<ProjectMember> = [
    {
      title: "ID",
      key: "user",
      align: "justify",
      render: (_, m) => (
        <span className="text-muted">
          {`#${m.user_snapshot?.id}`}
        </span>
      )

    },
    {
      title: "User",
      key: "user",
      render: (_, m) => (
        <div className="d-flex align-items-center gap-2">
          <UserAvatar
            name={m.user_snapshot?.name}
            img={m.user_snapshot?.img}
            size={32}
          />
          <div>
            <div className="fw-semibold">
              {m.user_snapshot?.name || `User #${m.user_id}`}
            </div>
            <div className="text-muted small">
              {m.user_snapshot?.email || ""}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 220,
      render: (role, m) => {
        if (editingId === m.id) {
          return (
            <div className="d-flex align-items-center gap-2">
              <Select
                style={{ width: 140 }}
                value={editingRole}
                onChange={(v) => setEditingRole(v as ProjectMemberRole)}
                options={PROJECT_MEMBER_ROLES.map((r) => ({
                  value: r.value,
                  label: r.label,
                }))}
              />
              <button
                type="button"
                className="btn btn-sm btn-success"
                onClick={saveRole}
                disabled={savingId === m.id}
              >
                Save
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={cancelEdit}
              >
                Cancel
              </button>
            </div>
          );
        }
        return <Tag>{role}</Tag>;
      },
    },
    {
      title: "Joined",
      dataIndex: "joined_at",
      key: "joined_at",
      width: 160,
      render: (joined) => (
        <span className="badge badge-sm badge-secondary-transparent">
          {joined ? humanReadableDate(joined) : "—"}
        </span>
      )

    },
    {
      title: "",
      key: "actions",
      width: 60,
      align: "center",
      render: (_, m) => {
        if (!canManage && !canChangeRole) return null;
        const items: MenuProps["items"] = [];
        if (canChangeRole) {
          items.push({
            key: "edit",
            label: "Change role",
            onClick: () => startEdit(m),
          });
        }
        if (canManage) {
          if (items.length > 0) items.push({ type: "divider" });
          items.push({
            key: "remove",
            label: <span className="text-danger">Remove from project</span>,
            onClick: () => {
              Modal.confirm({
                title: `Remove ${m.user_snapshot?.name || "this member"}?`,
                content: "They will lose access to this project.",
                okText: "Remove",
                okButtonProps: { danger: true },
                onOk: () => onRemove(m),
              });
            },
          });
        }
        if (items.length === 0) return null;
        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <button type="button" className="btn btn-sm btn-light">
              <i className="ti ti-dots-vertical" />
            </button>
          </Dropdown>
        );
      },
    },
  ];

  const visibleColumns = showActionsColumn ? columns : columns.filter((c) => c.key !== "actions");

  return (
    <Table
      rowKey="id"
      columns={visibleColumns}
      dataSource={members}
      loading={loading}
      pagination={false}
      size="middle"
    />
  );
};

export default MembersTable;
